import { useState, useEffect, useRef } from 'react'
import './PlaceSearch.css'
import { fuzzyMatch } from '../utils/soundex'

interface Place {
  name: string
  type: string
  displayType: string
  coordinates: [number, number]
}

interface PlaceWithScore extends Place {
  score: number
  matchType: string
}

interface PlaceSearchProps {
  onSelectPlace: (coordinates: [number, number], name: string) => void
}

function PlaceSearch({ onSelectPlace }: PlaceSearchProps) {
  const [places, setPlaces] = useState<Place[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredPlaces, setFilteredPlaces] = useState<PlaceWithScore[]>([])
  const [showResults, setShowResults] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  // Load place names on mount
  useEffect(() => {
    fetch('/data/place-names.geojson')
      .then(res => res.json())
      .then(data => {
        const placeList = data.features.map((f: any) => ({
          name: f.properties.name,
          type: f.properties.type,
          displayType: f.properties.displayType,
          coordinates: f.geometry.coordinates as [number, number]
        }))
        setPlaces(placeList)
      })
      .catch(err => console.error('Failed to load place names:', err))
  }, [])

  // Filter places based on search term with fuzzy matching
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPlaces([])
      setShowResults(false)
      return
    }

    const term = searchTerm.toLowerCase().trim()
    
    // Apply fuzzy matching to all places
    const matchedPlaces = places
      .map(place => {
        const match = fuzzyMatch(term, place.name)
        return {
          ...place,
          score: match.score,
          matchType: match.matchType
        }
      })
      .filter(p => p.score > 0)
      .sort((a, b) => {
        // First sort by match score (higher is better)
        if (Math.abs(a.score - b.score) > 0.05) {
          return b.score - a.score
        }
        
        // Then sort by type priority (islands, cities, towns first)
        const typePriority: { [key: string]: number } = {
          island: 1, city: 2, town: 3, village: 4, bay: 5, marina: 6,
          shoal: 7, reef: 8, rock: 9, wreck: 10
        }
        const aPriority = typePriority[a.type] || 99
        const bPriority = typePriority[b.type] || 99
        if (aPriority !== bPriority) return aPriority - bPriority
        
        // Finally sort alphabetically
        return a.name.localeCompare(b.name)
      })
      .slice(0, 10)  // Limit to 10 results

    setFilteredPlaces(matchedPlaces)
    setShowResults(matchedPlaces.length > 0)
  }, [searchTerm, places])

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectPlace = (place: Place) => {
    onSelectPlace(place.coordinates, place.name)
    setSearchTerm('')
    setShowResults(false)
  }

  const handleClear = () => {
    setSearchTerm('')
    setShowResults(false)
  }

  return (
    <div className="place-search" ref={searchRef}>
      <div className="search-input-wrapper">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          className="search-input"
          placeholder="Search islands, bays, marinas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm && setShowResults(true)}
        />
        {searchTerm && (
          <button className="clear-btn" onClick={handleClear}>
            ✕
          </button>
        )}
      </div>

      {showResults && (
        <div className="search-results">
          {filteredPlaces.map((place, idx) => (
            <div
              key={idx}
              className="search-result-item"
              onClick={() => handleSelectPlace(place)}
            >
              <div className="place-info">
                <div className="place-name">
                  {place.name}
                  {place.matchType === 'soundex' && (
                    <span className="match-indicator" title="Phonetic match">🔊</span>
                  )}
                  {place.matchType === 'fuzzy' && (
                    <span className="match-indicator" title="Similar spelling">≈</span>
                  )}
                </div>
              </div>
              <div className="place-type">{place.displayType}</div>
            </div>
          ))}
          {filteredPlaces.length === 0 && searchTerm && (
            <div className="no-results">No places found</div>
          )}
        </div>
      )}
    </div>
  )
}

export default PlaceSearch
